import React from 'react';
import { useBranding } from '../../contexts/BrandingContext';
import { useCRM } from '../../contexts/CRMContext';
import {
    CheckCircleIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    UserIcon,
    CalendarIcon,
    DocumentTextIcon,
    ArrowLeftIcon,
    InformationCircleIcon,
    CheckIcon,
    XMarkIcon,
    ArrowRightIcon,
    StarIcon,
    FlagIcon
} from '@heroicons/react/24/outline';
import { Button } from '../ui/button';
import { StatusBadge } from '../kibo-ui/table';

const TaskDetailView = ({ task, onClose }) => {
    const { branding } = useBranding();
    const { currentAgency } = useCRM();

    // Get personalized colors based on agency branding
    const getPersonalizedColors = () => {
        const primaryColor = branding.primaryColor || '#3B82F6';
        const secondaryColor = branding.secondaryColor || '#10B981';
        
        return {
            primary: primaryColor,
            secondary: secondaryColor,
            primaryLight: `${primaryColor}20`,
            primaryDark: `${primaryColor}80`,
            secondaryLight: `${secondaryColor}20`,
            secondaryDark: `${secondaryColor}80`
        };
    };

    const personalizedColors = getPersonalizedColors();

    // Mock task steps - in real implementation, this would come from the task data
    const getTaskSteps = () => {
        const baseSteps = [
            { id: 1, title: 'Initial Review', description: 'Your documents are being reviewed by our team', status: 'completed', completedAt: '2024-01-15' },
            { id: 2, title: 'Document Verification', description: 'Verifying all required documents are in order', status: 'completed', completedAt: '2024-01-16' },
            { id: 3, title: 'Application Submission', description: 'Submitting your application to the relevant authorities', status: 'in_progress', estimatedCompletion: '2024-01-20' },
            { id: 4, title: 'Follow-up & Updates', description: 'Monitoring progress and providing regular updates', status: 'pending' },
            { id: 5, title: 'Final Approval', description: 'Receiving final approval and completion', status: 'pending' }
        ];

        // Adjust steps based on task status
        if (task?.status === 'completed') {
            return baseSteps.map(step => ({ ...step, status: 'completed' }));
        } else if (task?.status === 'in_progress') {
            return baseSteps.map((step, index) => {
                if (index < 2) return { ...step, status: 'completed' };
                if (index === 2) return { ...step, status: 'in_progress' };
                return step;
            });
        }
        return baseSteps;
    };

    const taskSteps = getTaskSteps();

    // Status colors
    const getStatusColor = (status) => {
        const colors = {
            'completed': 'bg-green-100 text-green-700 border-green-200',
            'in_progress': 'bg-blue-100 text-blue-700 border-blue-200',
            'not_started': 'bg-muted text-foreground border-border',
            'pending': 'bg-yellow-100 text-yellow-700 border-yellow-200',
            'overdue': 'bg-red-100 text-red-700 border-red-200',
            'cancelled': 'bg-muted text-foreground border-border',
            'blocked': 'bg-red-100 text-red-700 border-red-200',
            'draft': 'bg-muted text-foreground border-border'
        };
        return colors[status] || 'bg-muted text-foreground border-border';
    };

    // Priority colors
    const getPriorityColor = (priority) => {
        const colors = {
            'high': 'bg-red-100 text-red-700 border-red-200',
            'medium': 'bg-yellow-100 text-yellow-700 border-yellow-200',
            'low': 'bg-green-100 text-green-700 border-green-200',
            'urgent': 'bg-purple-100 text-purple-700 border-purple-200'
        };
        return colors[priority] || 'bg-muted text-foreground border-border';
    };

    // Step status colors
    const getStepStatusColor = (status) => {
        const colors = {
            'completed': 'bg-green-50 text-green-700 border-green-200',
            'in_progress': 'bg-blue-50 text-blue-700 border-blue-200',
            'pending': 'bg-muted text-muted-foreground border-border'
        };
        return colors[status] || 'bg-muted text-muted-foreground border-border';
    };

    // Format status for display
    const formatStatus = (status) => {
        if (!status) return 'Unknown';
        return status
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    // Format priority for display
    const formatPriority = (priority) => {
        if (!priority) return 'Normal';
        return priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase();
    };

    // Format date for display
    const formatDate = (date) => {
        if (!date) return 'No due date';
        try {
            const dateObj = new Date(date);
            if (isNaN(dateObj.getTime())) return 'Invalid date';
            return dateObj.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch (error) {
            return 'Invalid date';
        }
    };

    // Format datetime for display
    const formatDateTime = (date) => {
        if (!date) return 'No date';
        try {
            const dateObj = new Date(date);
            if (isNaN(dateObj.getTime())) return 'Invalid date';
            return dateObj.toLocaleString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return 'Invalid date';
        }
    };

    // Get progress percentage
    const getProgressPercentage = () => {
        const completedSteps = taskSteps.filter(step => step.status === 'completed').length;
        return Math.round((completedSteps / taskSteps.length) * 100);
    };

    if (!task) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-muted-foreground" />
                    <h3 className="mt-2 text-sm font-medium text-foreground">No task selected</h3>
                    <p className="mt-1 text-sm text-muted-foreground">Please select a task to view its details.</p>
                </div>
            </div>
        );
    }

    const progressPercentage = getProgressPercentage();

    return (
        <div className="max-w-6xl mx-auto bg-card rounded-2xl shadow-xl border border-border overflow-hidden">
            {/* Header with Gradient Background */}
            <div 
                className="relative px-8 py-6 text-white"
                style={{
                    background: `linear-gradient(135deg, ${personalizedColors.primary} 0%, ${personalizedColors.secondary} 100%)`
                }}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-card/20 rounded-lg transition-colors"
                        >
                            <ArrowLeftIcon className="h-6 w-6" />
                        </button>
                        <div>
                            <h1 className="text-3xl font-bold">
                                {task.templateId?.title || task.title || 'Untitled Task'}
                            </h1>
                            <p className="text-white/80 mt-1">
                                {task.templateId?.category || task.category || 'General Task'}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-3">
                        <StatusBadge 
                            status={task.status}
                            className={`px-4 py-2 text-sm font-semibold border-2 ${getStatusColor(task.status)}`}
                        >
                            {formatStatus(task.status || 'not_started')}
                        </StatusBadge>
                        <StatusBadge 
                            status={task.priority}
                            className={`px-4 py-2 text-sm font-semibold border-2 ${getPriorityColor(task.priority)}`}
                        >
                            {formatPriority(task.priority || 'medium')}
                        </StatusBadge>
                    </div>
                </div>
            </div>

            {/* Progress Bar */}
            <div className="px-8 py-4 bg-muted border-b border-border">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-foreground">Progress</span>
                    <span className="text-sm font-semibold" style={{ color: personalizedColors.primary }}>
                        {progressPercentage}%
                    </span>
                </div>
                <div className="w-full bg-muted rounded-full h-3">
                    <div 
                        className="h-3 rounded-full transition-all duration-500 ease-out"
                        style={{ 
                            width: `${progressPercentage}%`,
                            backgroundColor: personalizedColors.primary
                        }}
                    />
                </div>
            </div>

            {/* Main Content */}
            <div className="px-8 py-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content - Task Steps */}
                    <div className="lg:col-span-2">
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-foreground mb-2">Task Progress</h2>
                            <p className="text-muted-foreground">
                                {task.templateId?.description || task.description || 'Track your task progress through each step.'}
                            </p>
                        </div>

                        {/* Steps Timeline */}
                        <div className="space-y-6">
                            {taskSteps.map((step, index) => (
                                <div key={step.id} className="relative">
                                    {/* Connection Line */}
                                    {index < taskSteps.length - 1 && (
                                        <div className="absolute left-6 top-12 w-0.5 h-16 bg-muted" />
                                    )}
                                    
                                    <div className={`relative flex items-start space-x-4 p-6 rounded-xl border-2 transition-all duration-200 ${getStepStatusColor(step.status)}`}>
                                        {/* Step Icon */}
                                        <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center border-2 ${
                                            step.status === 'completed' 
                                                ? 'bg-green-500 border-green-500 text-white' 
                                                : step.status === 'in_progress'
                                                ? 'bg-blue-500 border-blue-500 text-white'
                                                : 'bg-muted border-input text-muted-foreground'
                                        }`}>
                                            {step.status === 'completed' ? (
                                                <CheckIcon className="h-6 w-6" />
                                            ) : step.status === 'in_progress' ? (
                                                <ClockIcon className="h-6 w-6" />
                                            ) : (
                                                <span className="text-sm font-semibold">{step.id}</span>
                                            )}
                                        </div>

                                        {/* Step Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-lg font-semibold text-foreground">
                                                    {step.title}
                                                </h3>
                                                {step.status === 'completed' && step.completedAt && (
                                                    <span className="text-sm text-green-600 font-medium">
                                                        Completed {formatDate(step.completedAt)}
                                                    </span>
                                                )}
                                                {step.status === 'in_progress' && step.estimatedCompletion && (
                                                    <span className="text-sm text-blue-600 font-medium">
                                                        Due {formatDate(step.estimatedCompletion)}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="mt-2 text-muted-foreground">
                                                {step.description}
                                            </p>
                                            
                                            {/* Status Badge */}
                                            <div className="mt-3">
                                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                                    step.status === 'completed' 
                                                        ? 'bg-green-100 text-green-800'
                                                        : step.status === 'in_progress'
                                                        ? 'bg-blue-100 text-blue-800'
                                                        : 'bg-muted text-muted-foreground'
                                                }`}>
                                                    {step.status === 'completed' ? 'Completed' : 
                                                     step.status === 'in_progress' ? 'In Progress' : 'Pending'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Task Overview */}
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-border">
                            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                                <InformationCircleIcon className="h-5 w-5 mr-2" style={{ color: personalizedColors.primary }} />
                                Task Overview
                            </h3>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-muted-foreground">Due Date</span>
                                    <span className="text-sm font-semibold text-foreground">
                                        {formatDate(task.dueDate || task.due_date)}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-muted-foreground">Created</span>
                                    <span className="text-sm font-semibold text-foreground">
                                        {formatDate(task.createdAt)}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-muted-foreground">Steps Completed</span>
                                    <span className="text-sm font-semibold" style={{ color: personalizedColors.primary }}>
                                        {taskSteps.filter(s => s.status === 'completed').length} of {taskSteps.length}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Contact Information */}
                        <div className="bg-card border border-border rounded-xl p-6">
                            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                                <UserIcon className="h-5 w-5 mr-2" style={{ color: personalizedColors.primary }} />
                                Your Contact
                            </h3>
                            <div className="space-y-3">
                                {task.assignedTo && (
                                    <div className="flex items-center space-x-3">
                                        <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                                            <UserIcon className="h-5 w-5 text-muted-foreground" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-foreground">
                                                {task.assignedTo.name || 'Your Agent'}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {task.assignedTo.email || 'agent@example.com'}
                                            </p>
                                        </div>
                                    </div>
                                )}
                                <div className="pt-3 border-t border-border">
                                    <p className="text-xs text-muted-foreground mb-2">Need help with this task?</p>
                                    <button 
                                        className="text-sm font-medium transition-colors"
                                        style={{ color: personalizedColors.primary }}
                                    >
                                        Contact your agent →
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Important Notes */}
                        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                            <h3 className="text-lg font-semibold text-yellow-800 mb-3 flex items-center">
                                <FlagIcon className="h-5 w-5 mr-2" />
                                Important Notes
                            </h3>
                            <ul className="space-y-2 text-sm text-yellow-700">
                                <li className="flex items-start">
                                    <span className="text-yellow-500 mr-2">•</span>
                                    Please ensure all required documents are ready
                                </li>
                                <li className="flex items-start">
                                    <span className="text-yellow-500 mr-2">•</span>
                                    Check your email regularly for updates
                                </li>
                                <li className="flex items-start">
                                    <span className="text-yellow-500 mr-2">•</span>
                                    Contact us if you have any questions
                                </li>
                            </ul>
                        </div>

                        {/* Due Date Alert */}
                        {task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'completed' && (
                            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                                <div className="flex items-center space-x-3">
                                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                                    <div>
                                        <p className="text-sm font-semibold text-red-900">Overdue</p>
                                        <p className="text-xs text-red-700 mt-1">
                                            This task was due on {formatDate(task.dueDate)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TaskDetailView;
